import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MoneyInput } from "@/components/common/MoneyInput";
import { ArrendamientoResumen } from "@/components/solicitudes/ArrendamientoResumen";
import { api } from "@/lib/api";
import { mxn } from "@/lib/format";
import { estaCancelada, estaSellada } from "@/lib/selloSolicitud";
import { calcularBien, descripcionGarantia, FUENTE_PAGO_ARRENDAMIENTO, GARANTIA_ARRENDAMIENTO } from "@/lib/arrendamiento";

const Campo = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="mb-1 block text-xs font-medium text-slate-600">{label}</Label>
    {children}
  </div>
);

const Fijo = ({ testid, children }) => (
  <div data-testid={testid}
    className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-600">
    <Lock className="h-3.5 w-3.5 shrink-0 text-slate-400" /><span className="truncate">{children}</span>
  </div>
);

const TEXTOS = [["descripcion_bien", "Descripción del bien *", true], ["marca", "Marca *"], ["modelo", "Modelo *"],
  ["anio", "Año *"], ["numero_serie", "Número de serie *"]];

function useProveedores(activo) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (activo) api.get("/arrendamiento/proveedores").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [activo]);
  return items;
}

// Bloque «Datos del bien» de la solicitud cuando el producto es de arrendamiento.
export function ArrendamientoCaptura({ form, onChange, producto }) {
  const a = form.arrendamiento && typeof form.arrendamiento === "object" ? form.arrendamiento : {};
  const bloqueada = estaSellada(form) || estaCancelada(form);
  const proveedores = useProveedores(!bloqueada);
  if (bloqueada) return <ArrendamientoResumen arrendamiento={a} testid="arrendamiento-sellado" />;

  const calc = calcularBien(a);
  const set = (patch) => {
    const next = { ...a, ...patch };
    onChange("arrendamiento", { ...next, ...calcularBien(next) });
  };
  const engMinPct = Number(producto?.enganche_min_pct || 0);
  const engMin = Number(a.valor_bien_sin_iva) > 0 ? Math.round(Number(a.valor_bien_sin_iva) * engMinPct) / 100 : null;
  const rmin = producto?.residual_min_pct, rmax = producto?.residual_max_pct;
  const residualFuera = a.residual_pct !== "" && a.residual_pct != null
    && (Number(a.residual_pct) < Number(rmin) || Number(a.residual_pct) > Number(rmax));
  return (
    <div data-testid="arrendamiento-captura" className="grid grid-cols-1 gap-3 rounded-lg border border-teal-200 bg-teal-50/30 p-3 sm:grid-cols-2">
      <Campo label="Proveedor del bien *" full>
        <Select value={a.proveedor_id || ""} onValueChange={(v) => {
          const p = proveedores.find((x) => x.id === v);
          set({ proveedor_id: v, proveedor_nombre: p?.nombre || "", proveedor_rfc: p?.rfc || "" });
        }}>
          <SelectTrigger data-testid="arrendamiento-proveedor"><SelectValue placeholder="Elige un proveedor de arrendamiento..." /></SelectTrigger>
          <SelectContent>
            {proveedores.length === 0 && <div className="px-2 py-1.5 text-xs text-amber-600" data-testid="arrendamiento-sin-proveedores">No hay clientes marcados como proveedor de arrendamiento.</div>}
            {proveedores.map((p) => (
              <SelectItem key={p.id} value={p.id} disabled={p.bloqueado} data-testid={`arrendamiento-proveedor-opt-${p.id}`}>
                {p.nombre} · {p.rfc}{p.bloqueado ? " (bloqueado)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Campo>
      {TEXTOS.map(([k, label, full]) => (
        <Campo key={k} label={label} full={full}>
          <Input data-testid={`arrendamiento-${k.replace(/_/g, "-")}`} value={a[k] ?? ""}
            type={k === "anio" ? "number" : "text"}
            onChange={(e) => set({ [k]: k === "numero_serie" ? e.target.value.toUpperCase() : e.target.value })} />
        </Campo>
      ))}
      <Campo label="Valor del bien sin IVA *">
        <MoneyInput data-testid="arrendamiento-valor-sin-iva" value={a.valor_bien_sin_iva ?? ""} onChange={(v) => set({ valor_bien_sin_iva: v })} />
      </Campo>
      <Campo label="IVA del bien (16%)"><Fijo testid="arrendamiento-iva-bien">{calc.iva_bien != null ? mxn(calc.iva_bien) : "—"}</Fijo></Campo>
      <Campo label="Valor del bien con IVA"><Fijo testid="arrendamiento-valor-con-iva">{calc.valor_bien_con_iva != null ? mxn(calc.valor_bien_con_iva) : "—"}</Fijo></Campo>
      <Campo label={`Enganche — mínimo del producto ${engMinPct}%${engMin != null ? ` (${mxn(engMin)})` : ""}`}>
        <MoneyInput data-testid="arrendamiento-enganche" value={a.enganche ?? ""} onChange={(v) => set({ enganche: v })} />
      </Campo>
      <Campo label={`Valor residual (%) — rango del producto ${rmin ?? "—"}% a ${rmax ?? "—"}% *`}>
        <Input data-testid="arrendamiento-residual-pct" type="number" step="0.01" value={a.residual_pct ?? ""}
          onChange={(e) => set({ residual_pct: e.target.value })} className={residualFuera ? "border-red-400" : ""} />
        {residualFuera && <p className="mt-1 text-[11px] text-red-600">Fuera del rango del producto.</p>}
      </Campo>
      <Campo label="Valor residual ($)"><Fijo testid="arrendamiento-valor-residual">{calc.valor_residual != null ? mxn(calc.valor_residual) : "—"}</Fijo></Campo>
      <Campo label="Capital a arrendar (= monto solicitado)"><Fijo testid="arrendamiento-capital">{calc.capital_arrendar != null ? mxn(calc.capital_arrendar) : "—"}</Fijo></Campo>
      <Campo label="Plazo y periodicidad"><Fijo testid="arrendamiento-plazo">{form.plazo ? `${form.plazo} pagos` : "Elige el plazo en Condiciones"} · {producto?.periodicidad || "—"}</Fijo></Campo>
      <Campo label="Fuente de pago"><Fijo testid="arrendamiento-fuente-pago">{FUENTE_PAGO_ARRENDAMIENTO}</Fijo></Campo>
      <Campo label="Garantía" full>
        <Fijo testid="arrendamiento-garantia">{GARANTIA_ARRENDAMIENTO} — {descripcionGarantia(a) || "…"}</Fijo>
      </Campo>
      <p className="sm:col-span-2 text-[11px] text-slate-500">
        El monto solicitado es el capital a arrendar (valor sin IVA menos enganche); el IVA del bien, el valor residual y el capital los calcula el sistema al guardar.
      </p>
    </div>
  );
}
